/* ─── Data Visualization: Table, PlotlyFigure, MetricCard, Summary ─── */

import { useEffect, useMemo, useRef, useState } from 'react';
import type * as PlotlyNamespace from 'plotly.js';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import ReactMarkdown from 'react-markdown';

import { useArtifactSendEvent } from '../store';
import { usePaperTheme } from '@/hooks/useThemeFlag';

// ─── Table ──────────────────────────────────────────────────────────────

export function ArtifactTable({
  rows: rowsProp,
  settings,
  __node_id,
}: {
  rows?: Record<string, unknown>[];
  settings?: {
    show_columns?: string[] | 'all';
    sortable?: boolean;
    filterable?: boolean;
    paginated?: boolean;
  };
  __node_id?: string;
}) {
  const sendEvent = useArtifactSendEvent();
  const rows = Array.isArray(rowsProp) ? rowsProp : [];
  const showColumns = settings?.show_columns;
  const columnNames = Array.isArray(showColumns) ? showColumns : undefined;
  const sortable = settings?.sortable ?? true;
  const filterable = settings?.filterable ?? false;
  const paginated = settings?.paginated ?? false;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const cols: ColumnDef<Record<string, unknown>, unknown>[] = useMemo(() => {
    const keys = columnNames ?? (rows[0] ? Object.keys(rows[0]) : []);
    const helper = createColumnHelper<Record<string, unknown>>();
    return keys.map((key, i) => {
      const safeId = key.trim() !== '' ? key : `col_${i}`;
      return helper.accessor((row) => row[key], {
        id: safeId,
        header: key.trim() !== '' ? key : '',
        cell: (info) => {
          const raw = String(info.getValue() ?? '');
          return <ReactMarkdown>{raw}</ReactMarkdown>;
        },
      });
    });
  }, [columnNames, rows]);

  const table = useReactTable({
    data: rows,
    columns: cols,
    state: { sorting, globalFilter },
    onSortingChange: sortable ? setSorting : undefined,
    onGlobalFilterChange: filterable ? setGlobalFilter : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    getFilteredRowModel: filterable ? getFilteredRowModel() : undefined,
    getPaginationRowModel: paginated ? getPaginationRowModel() : undefined,
  });

  return (
    <div className="a-table-wrapper">
      {filterable && (
        <input
          type="text"
          className="a-table-filter"
          placeholder="Filter…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      )}
      <div className="a-table-scroll">
        <table className="a-table">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                    style={{ cursor: sortable ? 'pointer' : 'default' }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc'
                      ? ' ↑'
                      : header.column.getIsSorted() === 'desc'
                        ? ' ↓'
                        : ''}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="a-table-row"
                onClick={() => {
                  if (__node_id)
                    sendEvent(__node_id, 'row_click', { kind: 'TableRowClicked', row: row.original, index: row.index });
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginated && (
        <div className="a-table-pagination">
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            ← Prev
          </button>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── PlotlyFigure ──────────────────────────────────────────────────────

type PlotlyModule = typeof PlotlyNamespace;

let plotlyPromise: Promise<PlotlyModule> | null = null;
function loadPlotly(): Promise<PlotlyModule> {
  if (!plotlyPromise) {
    plotlyPromise = import('plotly.js-dist-min').then(
      (m) => ('default' in m ? m.default : m) as PlotlyModule,
    );
  }
  return plotlyPromise;
}

const DEFAULT_PLOTLY_HEIGHT = 420;

// ─── Plotly event normalization ────────────────────────────────────────

const PLOTLY_POINT_KEYS = [
  'curveNumber', 'pointNumber', 'pointIndex', 'pointNumbers',
  'x', 'y', 'z', 'lat', 'lon', 'label', 'value', 'text', 'customdata',
] as const;

const PLOTLY_EVENT_KIND: Record<string, string> = {
  plotly_click: 'PlotlyTraceClicked',
  plotly_selected: 'PlotlyTraceSelected',
};

function normalizePlotlyPoint(point: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of PLOTLY_POINT_KEYS) {
    if (key in point) result[key] = point[key];
  }
  return result;
}

/** Serialize a native MouseEvent or Plotly PointerEvent into a plain object. */
function serializeMouseEvent(e: unknown): Record<string, unknown> | null {
  if (e == null || typeof e !== 'object') return null;
  const me = e as Record<string, unknown>;
  // Check it looks like a MouseEvent
  if (typeof me.clientX !== 'number') return null;
  return {
    type: me.type ?? 'click',
    clientX: me.clientX, clientY: me.clientY,
    screenX: me.screenX, screenY: me.screenY,
    offsetX: me.offsetX, offsetY: me.offsetY,
    pageX: me.pageX, pageY: me.pageY,
    button: me.button, buttons: me.buttons,
    altKey: me.altKey, ctrlKey: me.ctrlKey,
    metaKey: me.metaKey, shiftKey: me.shiftKey,
  };
}

/** Convert pixel coordinates to data coordinates using Plotly's internal layout. */
function pixelToData(
  graph: HTMLDivElement,
  plotArea: HTMLElement,
  clientX: number,
  clientY: number,
): { x: number | null; y: number | null } {
  const fullLayout = (graph as unknown as Record<string, unknown>)._fullLayout as Record<string, unknown> | undefined;
  if (!fullLayout) return { x: null, y: null };
  const rect = plotArea.getBoundingClientRect();
  const px = clientX - rect.left;
  const py = clientY - rect.top;
  const w = rect.width;
  const h = rect.height;
  const xaxis = fullLayout.xaxis as Record<string, unknown> | undefined;
  const yaxis = fullLayout.yaxis as Record<string, unknown> | undefined;
  const xRange = xaxis?.range as [number, number] | undefined;
  const yRange = yaxis?.range as [number, number] | undefined;
  const x = xRange ? xRange[0] + (px / w) * (xRange[1] - xRange[0]) : null;
  const y = yRange ? yRange[1] - (py / h) * (yRange[1] - yRange[0]) : null;
  return { x, y };
}

function normalizePlotlyEvent(eventName: string, event: unknown): Record<string, unknown> | null {
  if (event == null || typeof event !== 'object') return null;
  const payload = event as Record<string, unknown>;
  const kind = PLOTLY_EVENT_KIND[eventName] ?? eventName;
  const result: Record<string, unknown> = { kind };

  if (Array.isArray(payload.points)) {
    const points = payload.points
      .filter((p): p is Record<string, unknown> => p != null && typeof p === 'object')
      .map(normalizePlotlyPoint);
    if (eventName === 'plotly_click') {
      if (points.length === 0) return null;
      result.point = points[0];
    } else {
      result.points = points;
    }
  }
  if ('range' in payload) result.range = payload.range;
  if ('lassoPoints' in payload) result.lassoPoints = payload.lassoPoints;
  const mouseEvt = serializeMouseEvent(payload.event);
  if (mouseEvt) result.event = mouseEvt;
  return result;
}

// ─── PlotlyFigure component ───────────────────────────────────────────

// Plotly defaults for non-responsive mode
const PLOTLY_DEFAULT_WIDTH = 700;
const PLOTLY_DEFAULT_HEIGHT = 450;
// Plotly's default margins (used when layout.margin is not set)
const PLOTLY_DEFAULT_MARGIN = { t: 100, l: 80, r: 80, b: 80 };
// Our tight margins for sized charts
const SIZED_MARGIN = { t: 24, l: 40, r: 10, b: 30 };

// Size presets: large ≈ panel width minus margins, medium = 1/2, small = 1/3
const SIZE_WIDTHS: Record<string, number> = {
  small: 227,
  medium: 340,
  large: 680,
};

// ─── Theme palette (frontend-owned, applied on every Plotly render) ─

interface PlotlyPalette {
  template: string;
  fg: string;
  mutedText: string;
  axisLine: string;
  grid: string;
  hoverFg: string;
  hoverBg: string;
  fontFamily: string;
}

/**
 * Resolve the active artifact palette into concrete hex/rgba strings
 * Plotly can consume. Reads `--artifact-*` CSS variables off the
 * supplied container so the palette tracks whichever theme classes
 * are currently applied to the document — `theme-paper-light`,
 * legacy `dark`, or the default Ink surface.
 */
function readPlotlyPalette(host: HTMLElement): PlotlyPalette {
  const styles = window.getComputedStyle(host);
  const read = (name: string, fallback: string): string => {
    const v = styles.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  const isPaperLight = document.documentElement.classList.contains('theme-paper-light');
  const fg = read('--artifact-foreground', isPaperLight ? '#0A0A0A' : '#FFFDF9');
  const mutedText = read('--artifact-text-secondary', isPaperLight ? '#56575A' : '#B8B8B8');
  return {
    template: isPaperLight ? 'plotly_white' : 'plotly_dark',
    fg,
    mutedText,
    axisLine: isPaperLight ? 'rgba(10,10,10,0.10)' : '#323232',
    grid: isPaperLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,253,249,0.08)',
    hoverFg: fg,
    hoverBg: isPaperLight ? '#FFFDF9' : '#222222',
    fontFamily: read('--artifact-font', "'ABC Arizona Sans', system-ui, sans-serif"),
  };
}

/** Resolve the `var(--artifact-*)` placeholders Python inserts into
 *  the title HTML to concrete hex values, since Plotly's SVG title
 *  renderer can't evaluate CSS variables. Keep the token list in sync
 *  with `apps/replica/src/artifact/styles/s_plotly.py`. */
function resolveTitleTokens(text: unknown, palette: PlotlyPalette): unknown {
  if (typeof text !== 'string') return text;
  return text
    .replace(/var\(--artifact-foreground\)/g, palette.fg)
    .replace(/var\(--artifact-text-secondary\)/g, palette.mutedText);
}

/**
 * Merge the active theme palette into a Plotly layout, *overriding*
 * any color the figure already carries on the slots the frontend owns
 * (text fg, axis line, grid, hoverlabel, backgrounds). Structural
 * fields the figure set (margins, legend orientation, tick angles,
 * axis ranges, etc.) are preserved by spreading the existing nested
 * object first and only patching the color-bearing keys on top.
 */
function applyPlotlyPalette(
  baseLayout: Record<string, unknown>,
  palette: PlotlyPalette,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...baseLayout };
  merged.template = palette.template;
  merged.paper_bgcolor = 'rgba(0,0,0,0)';
  merged.plot_bgcolor = 'rgba(0,0,0,0)';

  const baseFont = (baseLayout.font as Record<string, unknown> | undefined) ?? {};
  merged.font = { family: palette.fontFamily, size: 12, ...baseFont, color: palette.fg };

  const baseLegend = (baseLayout.legend as Record<string, unknown> | undefined) ?? {};
  const baseLegendFont = (baseLegend.font as Record<string, unknown> | undefined) ?? {};
  merged.legend = {
    ...baseLegend,
    bgcolor: 'rgba(0,0,0,0)',
    font: { size: 11, ...baseLegendFont, color: palette.mutedText, family: palette.fontFamily },
  };

  const baseHover = (baseLayout.hoverlabel as Record<string, unknown> | undefined) ?? {};
  const baseHoverFont = (baseHover.font as Record<string, unknown> | undefined) ?? {};
  merged.hoverlabel = {
    ...baseHover,
    bgcolor: palette.hoverFg,
    font: { ...baseHoverFont, color: palette.hoverBg, family: palette.fontFamily },
  };

  const patchAxis = (key: 'xaxis' | 'yaxis') => {
    const baseAxis = (baseLayout[key] as Record<string, unknown> | undefined) ?? {};
    const baseTick = (baseAxis.tickfont as Record<string, unknown> | undefined) ?? {};
    merged[key] = {
      showline: true,
      zeroline: false,
      ...baseAxis,
      linecolor: palette.axisLine,
      gridcolor: palette.grid,
      tickfont: { size: 11, ...baseTick, color: palette.mutedText, family: palette.fontFamily },
    };
  };
  patchAxis('xaxis');
  patchAxis('yaxis');

  const baseTitle = baseLayout.title as Record<string, unknown> | string | undefined;
  if (baseTitle != null && baseTitle !== '') {
    if (typeof baseTitle === 'string') {
      merged.title = { text: resolveTitleTokens(baseTitle, palette) };
    } else {
      merged.title = { ...baseTitle, text: resolveTitleTokens(baseTitle.text, palette) };
    }
  }

  return merged;
}

export function ArtifactPlotlyFigure({
  traces,
  layout,
  config,
  size = 'large',
  __node_id,
}: {
  /** Plotly trace array. */
  traces?: unknown[];
  /** Plotly layout dict. */
  layout?: Record<string, unknown>;
  /** PlotlySettings: display_mode_bar, scroll_zoom, responsive, static_plot. */
  config?: Record<string, unknown>;
  size?: string;
  __node_id?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotlyRef = useRef<PlotlyModule | null>(null);
  const sendEvent = useArtifactSendEvent();
  const [renderError, setRenderError] = useState<string | null>(null);
  // Track the active surface so the chart re-renders when the user
  // toggles light/dark; Plotly bakes colors at render time and won't
  // reflow them otherwise.
  const paperTheme = usePaperTheme();

  // Compute dimensions based on size preset, preserving the *plot area* aspect ratio
  const isFull = size === 'full';
  const sourceWidth = typeof layout?.width === 'number' ? layout.width as number : PLOTLY_DEFAULT_WIDTH;
  const sourceHeight = typeof layout?.height === 'number' ? layout.height as number : PLOTLY_DEFAULT_HEIGHT;
  const srcMargin = (layout?.margin ?? {}) as Record<string, number>;
  const srcT = srcMargin.t ?? PLOTLY_DEFAULT_MARGIN.t;
  const srcB = srcMargin.b ?? PLOTLY_DEFAULT_MARGIN.b;
  const srcL = srcMargin.l ?? PLOTLY_DEFAULT_MARGIN.l;
  const srcR = srcMargin.r ?? PLOTLY_DEFAULT_MARGIN.r;
  // Plot area = total - margins
  const plotAreaW = Math.max(1, sourceWidth - srcL - srcR);
  const plotAreaH = Math.max(1, sourceHeight - srcT - srcB);
  const plotAreaAspect = plotAreaH / plotAreaW;

  const targetWidth = isFull ? undefined : SIZE_WIDTHS[size] ?? SIZE_WIDTHS.large;
  const hasTitle = layout?.title != null && layout?.title !== '';
  const effectiveT = hasTitle ? 48 : SIZED_MARGIN.t;
  // For sized charts: target plot area width, then derive height, then add our margins back
  const plotWidth = targetWidth;
  const plotHeight = isFull
    ? (typeof layout?.height === 'number' ? layout.height as number : DEFAULT_PLOTLY_HEIGHT)
    : Math.round((targetWidth! - SIZED_MARGIN.l - SIZED_MARGIN.r) * plotAreaAspect + effectiveT + SIZED_MARGIN.b);

  useEffect(() => {
    let cancelled = false;
    const node = containerRef.current;
    if (!node || !traces) return;

    async function renderPlot() {
      try {
        const Plotly = plotlyRef.current ?? (await loadPlotly());
        if (cancelled || !containerRef.current) return;
        plotlyRef.current = Plotly;
        // Frontend owns the palette: every render we re-resolve the
        // active artifact CSS variables and override the slots the
        // figure shouldn't bake (text fg, axis line, grid, hoverlabel,
        // backgrounds, default colorway). Structural fields the figure
        // set (margins, legend orientation, axis ranges) are preserved.
        const palette = readPlotlyPalette(containerRef.current);
        // Clone so we don't mutate the layout prop the parent is
        // memoizing — Plotly mutates the layout it receives, but we
        // want subsequent renders to see the original input.
        const sourceLayout: Record<string, unknown> = { ...(layout ?? {}) };
        if (!('colorway' in sourceLayout)) sourceLayout.colorway = ['#7678E3'];
        const themedLayout = applyPlotlyPalette(sourceLayout, palette);

        const hasTitle = themedLayout.title != null && themedLayout.title !== '';
        const sizedMargin = { ...SIZED_MARGIN, t: hasTitle ? 48 : SIZED_MARGIN.t, pad: 0 };
        const effectiveLayout = isFull
          ? themedLayout
          : {
              ...themedLayout,
              width: plotWidth,
              height: plotHeight,
              margin: sizedMargin,
              autosize: false,
              title: themedLayout.title ? { ...(typeof themedLayout.title === 'object' ? themedLayout.title as object : { text: themedLayout.title }), automargin: false } : undefined,
            };
        await Plotly.react(
          containerRef.current,
          (Array.isArray(traces) ? traces : []) as PlotlyNamespace.Data[],
          effectiveLayout as Partial<PlotlyNamespace.Layout>,
          {
            displayModeBar: size === 'small' ? false : ((config?.display_mode_bar as boolean | 'hover') ?? 'hover'),
            displaylogo: false,
            modeBarButtonsToRemove: [
              'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'hoverClosestCartesian',
              'hoverCompareCartesian', 'toggleSpikelines',
            ] as PlotlyNamespace.ModeBarDefaultButtons[],
            doubleClick: false,
            scrollZoom: (config?.scroll_zoom as boolean) ?? false,
            responsive: isFull && ((config?.responsive as boolean) ?? true),
            staticPlot: size === 'small' || ((config?.static_plot as boolean) ?? false),
          } as Partial<PlotlyNamespace.Config>,
        );
        if (cancelled) return;
        setRenderError(null);

        // Neutralise Plotly's non-passive wheel listener that blocks page scroll.
        const dragLayer = containerRef.current.querySelector('.draglayer');
        if (dragLayer) {
          dragLayer.addEventListener(
            'wheel',
            (e) => { e.stopImmediatePropagation(); },
            { capture: true, passive: true },
          );
        }

        type PlotlyGraph = HTMLDivElement & {
          on: (name: string, handler: (event: unknown) => void) => void;
          removeAllListeners?: (name?: string) => void;
        };
        const graph = containerRef.current as PlotlyGraph;

        // Track whether a plotly_click (data point) fired in this tick
        let pointClickedInTick = false;

        for (const eventName of ['plotly_click', 'plotly_selected']) {
          graph.removeAllListeners?.(eventName);
          graph.on(eventName, (event: unknown) => {
            if (eventName === 'plotly_click') pointClickedInTick = true;
            if (!__node_id) return;
            const payload = normalizePlotlyEvent(eventName, event);
            if (payload) sendEvent(__node_id, eventName, payload);
          });
        }

        // Canvas click: native click on the plot area, only if no data point was clicked
        const plotArea = containerRef.current.querySelector('.nsewdrag') as HTMLElement | null;
        if (plotArea) {
          plotArea.addEventListener('click', (e: MouseEvent) => {
            // Reset on next microtask — plotly_click fires synchronously before native click
            setTimeout(() => { pointClickedInTick = false; }, 0);
            if (pointClickedInTick) return;
            if (!__node_id) return;
            const { x, y } = pixelToData(graph, plotArea, e.clientX, e.clientY);
            sendEvent(__node_id, 'plotly_canvas_click', {
              kind: 'PlotlyCanvasClicked', x, y,
              event: serializeMouseEvent(e),
            });
          });
        }

        if (isFull) {
          window.requestAnimationFrame(() => {
            void Plotly.Plots.resize(graph as unknown as PlotlyNamespace.PlotlyHTMLElement);
          });
        }
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        setRenderError(message);
        if (__node_id) {
          sendEvent(__node_id, 'error', { kind: 'PlotlyErrored', message });
        }
      }
    }

    void renderPlot();

    return () => {
      cancelled = true;
      type PlotlyGraph = HTMLDivElement & { removeAllListeners?: (n?: string) => void };
      const graph = containerRef.current as PlotlyGraph | null;
      graph?.removeAllListeners?.();
      if (plotlyRef.current && graph) {
        plotlyRef.current.purge(graph as unknown as PlotlyNamespace.PlotlyHTMLElement);
      }
    };
  }, [__node_id, traces, layout, config, size, isFull, plotWidth, plotHeight, sendEvent, paperTheme]);

  return (
    <div className="a-plotly-shell" style={isFull ? undefined : {
      display: 'inline-block',
      ...(size === 'small' ? { padding: '4px 4px 2px' } : {}),
    }}>
      <div ref={containerRef} className="a-plotly-canvas" style={{
        height: `${plotHeight}px`,
        ...(plotWidth ? { width: `${plotWidth}px` } : {}),
      }} />
      {renderError && <div className="a-plotly-error">Plotly render failed: {renderError}</div>}
    </div>
  );
}

// ─── MetricCard ─────────────────────────────────────────────────────────

import { formatTextual } from '../textual';

export function ArtifactMetricCard({
  value,
  label,
  delta,
  trend,
}: {
  value?: unknown;
  label?: unknown;
  delta?: unknown;
  trend?: string;
}) {
  const trendClass =
    trend === 'up'
      ? 'a-metric-up'
      : trend === 'down'
        ? 'a-metric-down'
        : 'a-metric-flat';

  return (
    <div className="a-metric-card">
      <div className="a-metric-value">{formatTextual(value)}</div>
      <div className="a-metric-label">{formatTextual(label)}</div>
      {delta != null && (
        <div className={`a-metric-delta ${trendClass}`}>
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {formatTextual(delta)}
        </div>
      )}
    </div>
  );
}

// ─── Summary ──────────────────────────────────────────────────────────

export function ArtifactSummary({
  data,
}: {
  data?: Record<string, unknown>;
}) {
  const entries = data ? Object.entries(data) : [];

  return (
    <div className="a-summary">
      {entries.map(([key, value]) => (
        <div key={key} className="a-summary-row">
          <div className="a-summary-key">{key.replaceAll('_', ' ')}</div>
          <div className="a-summary-value">{formatTextual(value)}</div>
        </div>
      ))}
    </div>
  );
}
